import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Phone, Mail, MapPin, Clock, Send, ArrowRight, MessageCircle
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

const WHATSAPP_URL = 'https://wa.me/919663112394?text=Hi!%20I%27m%20interested%20in%20getting%20a%20website%20for%20my%20business';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_type: '',
    urgency: '',
    message: ''
  });
  const [isWhatsApp, setIsWhatsApp] = useState(true);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const whatsappMsg = `Hi! I'm ${formData.name}.%0A%0AI'm interested in getting a website for my ${formData.business_type || 'business'}.%0A%0ATimeline: ${formData.urgency || 'Not specified'}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0A%0A${formData.message || ''}`;

    window.open(`https://wa.me/919663112394?text=${whatsappMsg}`, '_blank');
    toast.success('Opening WhatsApp! We\'ll respond within minutes.');

    setFormData({ name: '', email: '', phone: '', business_type: '', urgency: '', message: '' });
  };

  const businessTypes = [
    'Salon / Beauty',
    'Medical Clinic',
    'Spa / Wellness',
    'Fitness / Gym',
    'Consultant',
    'Other'
  ];

  const urgencyOptions = [
    'Urgent - Need it ASAP',
    'Within a week',
    'This month',
    'Just exploring'
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="pt-28 pb-16 lg:pt-36 lg:pb-20 bg-hero-gradient relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl" />
        <div className="container relative z-10">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-primary-50 text-primary-600 font-medium rounded-full text-sm mb-6">
              Get In Touch
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-neutral-900 mb-5 leading-[1.1]">
              Let's Build Something{' '}
              <span className="text-gradient-primary">Great Together</span>
            </h1>
            <p className="text-lg text-neutral-500">
              Ready to take your business online? Fill out the form or reach us directly via WhatsApp.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white" data-testid="contact-form-section">
        <div className="container">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Your full name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="you@example.com"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      Phone
                      <span className="text-neutral-400 ml-1 text-xs">(WhatsApp preferred)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="+91 98765 43210"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Business Type *</label>
                    <select
                      name="business_type"
                      value={formData.business_type}
                      onChange={handleChange}
                      required
                      className="input-field"
                      data-testid="input-business-type"
                    >
                      <option value="">Select type</option>
                      {businessTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Website Needed By</label>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="input-urgency"
                  >
                    <option value="">Select timeline</option>
                    {urgencyOptions.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="input-field resize-none"
                    placeholder="Tell us about your business and what you're looking for..."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-whatsapp w-full justify-center py-4 text-base"
                  data-testid="submit-button"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  Send via WhatsApp
                  <ArrowRight className="w-5 h-5" />
                </button>

                <p className="text-xs text-neutral-400 text-center">
                  This will open WhatsApp with your message pre-filled
                </p>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {/* Quick Contact Card */}
              <div className="bg-neutral-900 rounded-2xl p-7 text-white mb-6">
                <h3 className="font-heading text-xl font-bold mb-6">Contact Information</h3>
                <div className="space-y-5">
                  <a href="tel:+919663112394" className="flex items-start gap-4 hover:text-primary-300 transition-colors" data-testid="contact-phone">
                    <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-neutral-300">Phone</p>
                      <p className="text-white">+91 96631 12394</p>
                    </div>
                  </a>
                  <a href="mailto:hello@makemydukaan.in" className="flex items-start gap-4 hover:text-primary-300 transition-colors" data-testid="contact-email">
                    <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-neutral-300">Email</p>
                      <p className="text-white">hello@makemydukaan.in</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-neutral-300">Business Hours</p>
                      <p className="text-white">Mon - Sat: 10 AM - 7 PM IST</p>
                      <p className="text-neutral-400 text-sm">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-5 bg-[#25D366] rounded-2xl text-white hover:bg-[#20bd5a] transition-colors mb-6"
                data-testid="whatsapp-cta-card"
              >
                <FaWhatsapp className="w-10 h-10 flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-lg">WhatsApp Us</p>
                  <p className="text-white/80 text-sm">Fastest Response - Usually within 5 minutes</p>
                </div>
                <ArrowRight className="w-5 h-5 ml-auto flex-shrink-0" />
              </a>

              {/* Book Consultation */}
              <div className="card-base bg-primary-50 border-primary-100">
                <MessageCircle className="w-10 h-10 text-primary-500 mb-3" />
                <h4 className="font-heading font-bold text-neutral-900 mb-2">Book a Free Consultation</h4>
                <p className="text-neutral-500 text-sm mb-4">
                  15-minute call to discuss your needs. No commitment required.
                </p>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full justify-center text-sm py-3"
                  data-testid="book-consultation-btn"
                >
                  Schedule on WhatsApp
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-section-alt" data-testid="contact-faq-section">
        <div className="container">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl font-bold text-neutral-900">
              Common Questions
            </h2>
          </motion.div>
          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-5">
            {[
              { q: 'How long does it take?', a: 'Most websites go live within 48 hours of your initial brief.' },
              { q: 'Do you provide support?', a: 'Yes! All plans include ongoing support via WhatsApp and email.' },
              { q: 'Can I upgrade later?', a: 'Absolutely! Upgrade anytime and only pay the difference.' },
              { q: 'Do you work pan-India?', a: 'Yes! We serve businesses across India, everything is done remotely.' }
            ].map((faq, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-xl p-6 border border-neutral-100"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`contact-faq-${i}`}
              >
                <h4 className="font-heading font-bold text-neutral-900 mb-2">{faq.q}</h4>
                <p className="text-neutral-500 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
